<template>
  <el-dialog v-if="status"
    :title="title"
    :visible="visible"
    @update:visible="setVisibility($event)"
  >
    <el-alert
      v-if="errors.statusAlreadyProcessed.visible"
      :title="statusAlreadyProcessedErrorMessage"
      type="error"
      show-icon
      class="mb-1"
      @close="errors.statusAlreadyProcessed.visible = false"
    />

    <el-alert
      v-if="statusMismatches"
      type="warning" show-icon
      class="mb-1"
    >
      <strong slot="title">{{ statusMismatchErrorMessage }}</strong>
    </el-alert>

    <el-alert
      v-if="idMismatches"
      type="warning" show-icon
      :title="idMismatchesErrorMessage"
      class="mb-1"
    />

    <el-form
      ref="scanForm"
      :model="scanForm.data"
      :rules="scanForm.rules"
      @submit.native.prevent="onSubmit"
    >
      <el-form-item prop="id">
        <el-input
          ref="idInput"
          v-model="scanForm.data.id"
          autofocus
          clearable
        >
          <span slot="prepend">{{ idLabel }}</span>
          <el-button slot="append" native-type="submit" v-t="'common.submit'"/>
        </el-input>
      </el-form-item>
    </el-form>

    <div v-if="manufacturingItem">
      <p v-t="'components.scanDialog.text'"></p>

      <dx-data-grid
        :data-source="[manufacturingItem]"
        :columns="columns"
        :column-auto-width="true"
        :show-borders="true"
        :show-column-lines="true"
        class="mb-4"
      />

      <el-form
        v-if="manufacturingItemFormEnabled"
        ref="manufacturingItemForm"
        :model="manufacturingItemForm.data"
        :rules="manufacturingItemForm.rules"
        @submit.native.prevent="onConfirm"
      >
        <el-form-item prop="packageCount" :label="packageCountLabel">
          <el-button
            v-for="packageCount in packageCountButtonValues"
            :key="packageCount"
            @click="setPackageCount(packageCount)"
            class="package-count-button"
          >
            {{ packageCount }}
          </el-button>

          <el-input-number v-model="manufacturingItemForm.data.packageCount" class="ml-3"/>
        </el-form-item>
      </el-form>
    </div>

    <el-divider/>

    <el-button
      type="primary"
      @click="onConfirm"
      :disabled="confirmButtonDisabled"
      v-t="'common.confirm'"
    />
    <el-button @click="onCancel" v-t="'common.cancel'"/>
  </el-dialog>
</template>

<script>
import { DxDataGrid } from 'devextreme-vue';
import { getManufacturerStatuses } from '@/store';
import { buildColumns } from '@/lib/data-grid-columns';
import {
  getI18nPropertyKeys,
  getI18nValueKeys,
  matchesHttpError,
  previous,
  translateProperty,
  translateValue,
} from '@/lib/util';

export default {
  name: 'ScanDialog',
  components: {
    DxDataGrid,
  },
  props: {
    visible: { type: Boolean, default: false },
    status: { type: Object, default: null },
    manufacturingItemId: { type: Number, default: NaN },
  },
  data() {
    return {
      errors: {
        statusAlreadyProcessed: {
          visible: false,
        },
      },
      scanForm: {
        data: {
          id: '',
        },
        rules: {
          id: [
            { required: true, fullField: translateProperty('manufacturingItem', 'id') },
            {
              pattern: /^MI-\d+$/,
              message: this.$t('components.scanDialog.scanForm.rules.id.pattern.message'),
            },
          ],
        },
      },
      manufacturingItemForm: {
        data: {
          packageCount: undefined,
        },
        rules: {
          packageCount: [
            {
              required: true,
              type: 'number',
              fullField: translateProperty('manufacturingItem', 'packageCount'),
            },
          ],
        },
      },
      manufacturingItem: null,
      columns: [],
      packageCountButtonValues: [1, 2, 3, 4, 5],
    };
  },
  computed: {
    title() {
      return this.$t('components.scanDialog.title', {
        statusName: translateValue('status', 'name', this.status.name),
      });
    },
    idLabel() {
      return translateProperty('manufacturingItem', 'id');
    },
    packageCountLabel() {
      return translateProperty('manufacturingItem', 'packageCount');
    },
    id() {
      return parseInt(this.scanForm.data.id.replace(/^MI-/, ''), 10);
    },
    manufacturerStatuses() {
      if (!this.manufacturingItem) return [];
      return getManufacturerStatuses(this.$store.state, this.manufacturingItem.manufacturerId);
    },
    previousStatus() {
      return previous(this.manufacturerStatuses, this.status);
    },
    statusAlreadyProcessedErrorMessage() {
      return this.$t('components.scanDialog.errors.statusAlreadyProcessed');
    },
    statusMismatches() {
      const { manufacturingItem } = this;
      if (!manufacturingItem || !this.previousStatus) return false;

      return manufacturingItem.status.id !== this.previousStatus.id;
    },
    statusMismatchErrorMessage() {
      if (!this.previousStatus) return '';

      const previousStatusName = translateValue('status', 'name', this.previousStatus.name);

      return this.$t('components.scanDialog.errors.statusMismatches', {
        previousStatusName,
      });
    },
    idMismatches() {
      const { manufacturingItem } = this;
      if (!manufacturingItem) return false;

      return manufacturingItem.id !== this.id;
    },
    idMismatchesErrorMessage() {
      return this.$t('components.scanDialog.errors.idMismatches');
    },
    manufacturingItemFormEnabled() {
      return this.status.name === 'packaged';
    },
    confirmButtonDisabled() {
      return !this.manufacturingItem || this.idMismatches;
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.onOpen();
      } else {
        this.onClose();
      }
    },
  },
  methods: {
    buildColumns,
    getI18nPropertyKeys,
    getI18nValueKeys,
    configureColumns() {
      return this.buildColumns('manufacturingItem', [
        {
          property: 'id',
        },
        {
          property: 'shipment.acceptanceNumber',
          entity: 'shipment',
          captionContext: 'short',
        },
        {
          property: 'statusId',
          caption: this.$t('components.scanDialog.currentStatus'),
        },
        {
          dataField: 'nextStatus.id',
          property: 'statusId',
          caption: this.$t('components.scanDialog.nextStatus'),
        },
        {
          property: 'productNameId',
          captionContext: 'short',
        },
        {
          property: 'productCategoryId',
          captionContext: 'short',
        },
        {
          property: 'shipment.customer',
          entity: 'shipment',
        },
      ]);
    },
    setVisibility(value) {
      this.$emit('update:visible', value);
    },
    setPackageCount(value) {
      this.manufacturingItemForm.data.packageCount = value;
    },
    reset() {
      this.$refs.scanForm.resetFields();

      if (this.manufacturingItemFormEnabled) {
        this.$refs.manufacturingItemForm.resetFields();
      }

      this.manufacturingItem = null;
    },
    close() {
      this.setVisibility(false);
    },
    async getManufacturingItem(where) {
      const fields = {
        id: true,
        createdAt: true,
        productNameId: true,
        productCategoryId: true,
        manufacturerId: true,
        statusId: true,
        'status.id': true,
        'shipment.customer': true,
        'shipment.acceptanceNumber': true,
      };

      const { data } = await this.$http.get('/manufacturing-items', {
        params: { filter: { fields, where, order: ['createdAt DESC'], limit: 1 } },
      });

      if (data.length === 0) {
        this.$message({
          message: this.$t('common.errors.notFound', { entity: this.$t('entities.configuration.name') }),
          type: 'warning',
        });

        this.manufacturingItem = null;
      } else {
        const [manufacturingItem] = data;
        manufacturingItem.nextStatus = this.status;
        this.manufacturingItem = manufacturingItem;
      }
    },
    async pushStatus() {
      const payload = {
        manufacturingItemId: this.manufacturingItem.id,
        statusId: this.status.id,
      };

      if (this.manufacturingItemFormEnabled) {
        payload.packageCount = this.manufacturingItemForm.data.packageCount;
      }

      await this.$http.post('/manufacturing-item-status-updates', payload);

      this.$message(this.$t('components.scanDialog.messages.itemPushedToNextState'));
      this.$emit('pushed:status');
    },
    showResolveErrorsBeforeSubmitMessage() {
      this.$message({
        message: this.$t('common.form.resolveErrorsBeforeSubmit'),
        type: 'warning',
      });
    },
    async onSubmit() {
      const valid = await new Promise((resolve) => {
        this.$refs.scanForm.validate(resolve);
      });

      if (valid) {
        await this.getManufacturingItem({ id: this.id });
      } else {
        this.showResolveErrorsBeforeSubmitMessage();
      }
    },
    onReset() {
      this.reset();
    },
    async onConfirm() {
      if (this.manufacturingItemFormEnabled) {
        const valid = await new Promise((resolve) => {
          this.$refs.manufacturingItemForm.validate(resolve);
        });

        if (!valid) {
          this.showResolveErrorsBeforeSubmitMessage();
          return;
        }
      }

      try {
        await this.pushStatus();
        this.close();
      } catch (error) {
        if (matchesHttpError(error, 409, 'STATUS_ALREADY_PROCESSED')) {
          this.errors.statusAlreadyProcessed.visible = true;
        } else {
          throw error;
        }
      }
    },
    onCancel() {
      this.close();
    },
    async onOpen() {
      if (this.manufacturingItemId) {
        await this.getManufacturingItem({ id: this.manufacturingItemId });
        this.scanForm.data.id = `MI-${this.manufacturingItem.id}`;
      }

      this.$nextTick(() => {
        this.$refs.idInput.focus();
        this.$refs.idInput.select();
      });
    },
    onClose() {
      this.reset();
    },
  },
  created() {
    this.columns = this.configureColumns();
  },
};
</script>

<style scope>
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .package-count-button + .package-count-button {
    margin-left: 15px;
  }
</style>
